import {
  accumulationFactorForAges,
  assumptionSetDisclosure,
  getAgeWeightedAssumptionSet,
  monthlyAnnuityFactorAtAge,
} from './compliance/ageWeightedAssumptions.js'
import { computeEbar } from './compliance/crossTesting.js'

const cents = (value) => Math.round((Number(value) || 0) * 100)

function roundedProRata(totalCents, weightedRows) {
  const totalWeight = weightedRows.reduce((sum, row) => sum + row.weight, 0)
  if (!(totalCents > 0) || !(totalWeight > 0)) return weightedRows.map(() => 0)
  const raw = weightedRows.map((row, index) => {
    const exact = totalCents * row.weight / totalWeight
    const floor = Math.floor(exact)
    return { index, floor, fraction: exact - floor, id: String(row.employeeId ?? '') }
  })
  let remainder = totalCents - raw.reduce((sum, row) => sum + row.floor, 0)
  raw.sort((a, b) => b.fraction - a.fraction || a.id.localeCompare(b.id) || a.index - b.index)
  for (let index = 0; index < remainder; index += 1) raw[index].floor += 1
  return raw.sort((a, b) => a.index - b.index).map((row) => row.floor)
}

// Plan-wide eligibility AND the profit-sharing allocation conditions
// (minimum hours / last-day, computed in calculations.js) both gate PS.
const eligibleForAllocation = (employee) => employee?.eligibility?.allowsEmployerContributions
  && employee?.profitSharingConditionMet !== false

function baseHeadroomCents(employee) {
  const priorAdditions = Number(employee.baseDeferral || 0)
    + Number(employee.matchAmount || 0)
    + Number(employee.necAmount || 0)
    + Number(employee.qnecAmount || 0)
    + Number(employee.forfeitureAllocation || 0)
  return cents(Math.min(
    Math.max(0, Number(employee.limit415c) - priorAdditions),
    Math.max(0, Number(employee.cappedCompensation) - priorAdditions),
  ))
}

export function calculateAgeWeightedFactor(employee, component) {
  if (employee?.age === null || employee?.age === undefined || employee?.age === '') return null
  const currentAge = Number(employee?.age)
  const compensation = Number(employee?.cappedCompensation)
  const normalRetirementAge = Number(component?.normalRetirementAge)
  const assumptionSet = component?.assumptionSet
  if (!Number.isFinite(currentAge) || !(compensation > 0) || !Number.isFinite(normalRetirementAge)) return null
  const testingAge = Math.max(normalRetirementAge, currentAge)
  const annuityFactor = monthlyAnnuityFactorAtAge(testingAge, assumptionSet)
  const accumulationFactor = accumulationFactorForAges(currentAge, testingAge, assumptionSet)
  if (!(annuityFactor > 0) || !(accumulationFactor > 0)) return null
  return {
    currentAge,
    testingAge,
    annuityFactor,
    accumulationFactor,
    weight: compensation * annuityFactor / accumulationFactor,
  }
}

function requestAgeWeightedPool(employees = [], component = {}) {
  const requestedPoolCents = cents(component.contributionPool)
  const assumptionSet = getAgeWeightedAssumptionSet(component.assumptionSet)
  const eligible = employees.filter(eligibleForAllocation)
  const missingAgeEmployeeIds = eligible
    .filter((employee) => employee.age === null || employee.age === undefined || employee.age === '' || !Number.isFinite(Number(employee.age)))
    .map((employee) => employee.id)
  if (!assumptionSet || !Number.isFinite(Number(component.normalRetirementAge)) || missingAgeEmployeeIds.length) {
    return {
      status: 'unavailable', requestedPool: requestedPoolCents / 100, allocatedPool: 0,
      unallocatedResidual: requestedPoolCents / 100, assumptionSet: assumptionSetDisclosure(component.assumptionSet),
      missingAgeEmployeeIds, allocationsByEmployeeId: {},
      reason: !assumptionSet ? 'unsupported-assumption-set' : missingAgeEmployeeIds.length ? 'missing-age' : 'invalid-normal-retirement-age',
    }
  }
  const factors = eligible.map((employee) => ({ employee, ...calculateAgeWeightedFactor(employee, component) }))
    .filter((row) => row.weight > 0)
  const requestedCents = roundedProRata(requestedPoolCents, factors.map((row) => ({ employeeId: row.employee.id, weight: row.weight })))
  const allocationsByEmployeeId = {}
  factors.forEach((row, index) => {
    const requested = requestedCents[index]
    allocationsByEmployeeId[row.employee.id] = {
      requested: requested / 100,
      allocated: 0,
      weight: row.weight,
      factor: row.annuityFactor / row.accumulationFactor,
      testingAge: row.testingAge,
      capLimited: false,
    }
  })
  const ebars = factors.map((row, index) => computeEbar(
    requestedCents[index] / 100,
    row.employee.cappedCompensation,
    row.employee.age,
    { testingAge: row.testingAge, assumptionSet: component.assumptionSet },
  ))
  // Cent rounding can move displayed EBARs by a few millionths of a percentage
  // point even when the unrounded inverse factors are identical.
  const equalEbarBeforeCaps = ebars.length < 2 || Math.max(...ebars) - Math.min(...ebars) < 1e-5
  return {
    status: 'requested',
    requestedPool: requestedPoolCents / 100,
    allocatedPool: 0,
    unallocatedResidual: requestedPoolCents / 100,
    assumptionSet: assumptionSetDisclosure(component.assumptionSet),
    equalEbarBeforeCaps,
    capLimitedEmployeeCount: 0,
    missingAgeEmployeeIds,
    allocationsByEmployeeId,
  }
}

// --- Integrated / permitted disparity (§401(l)) -----------------------------
//
// The engine models a rate-funded integrated formula: basePercent applies to
// all §401(a)(17)-capped compensation and disparityPercent is the ADD-ON that
// applies only to compensation above the integration level, so the excess-comp
// rate is basePercent + disparityPercent. Validation follows 26 CFR
// §1.401(l)-2: violations are rejected with field-level errors and are never
// silently clamped.

export function resolveIntegrationLevel(integrationLevel = {}, taxableWageBase) {
  const twb = Number(taxableWageBase)
  if (!(twb > 0)) return null
  if (integrationLevel.type === 'custom_percent_of_twb') {
    const percent = Number(integrationLevel.value)
    return Number.isFinite(percent) && percent > 0 ? (percent / 100) * twb : null
  }
  if (integrationLevel.type === 'custom_dollar') {
    const dollars = Number(integrationLevel.value)
    return Number.isFinite(dollars) && dollars > 0 ? dollars : null
  }
  return twb
}

// Maximum disparity ladder from 26 CFR §1.401(l)-2(d): 5.7% at exactly the
// TWB or at levels of at most 20% of it, 5.4% above 80% and below 100%, and
// 4.3% above 20% through 80%. Levels above the TWB are invalid (null).
const BAND_EPSILON = 1e-9
export function maxIntegratedDisparityPercent(integrationLevelDollar, taxableWageBase) {
  const level = Number(integrationLevelDollar)
  const twb = Number(taxableWageBase)
  if (!(level > 0) || !(twb > 0)) return null
  const ratio = level / twb
  if (ratio > 1 + BAND_EPSILON) return null
  if (ratio >= 1 - BAND_EPSILON) return 5.7
  if (ratio > 0.8 + BAND_EPSILON) return 5.4
  if (ratio > 0.2 + BAND_EPSILON) return 4.3
  return 5.7
}

export function validateIntegratedComponent(component = {}, { taxableWageBase } = {}) {
  const errors = []
  const twb = Number(taxableWageBase)
  if (!(twb > 0)) {
    errors.push({ field: 'planYear', message: 'No taxable wage base is defined for this plan year, so an integrated formula cannot be validated.' })
    return errors
  }
  const base = Number(component.basePercent)
  const disparity = Number(component.disparityPercent)
  if (!Number.isFinite(base) || base < 0) {
    errors.push({ field: 'basePercent', message: 'Base percent must be a non-negative number.' })
  }
  if (!Number.isFinite(disparity) || disparity < 0) {
    errors.push({ field: 'disparityPercent', message: 'Disparity percent must be a non-negative number.' })
  }
  const level = component.integrationLevel || {}
  if (!['twb', 'custom_percent_of_twb', 'custom_dollar'].includes(level.type)) {
    errors.push({ field: 'integrationLevel', message: 'Integration level must be the taxable wage base, a percent of it, or a dollar amount.' })
    return errors
  }
  if (level.type === 'custom_percent_of_twb' && (!Number.isFinite(Number(level.value)) || Number(level.value) <= 0 || Number(level.value) > 100)) {
    errors.push({ field: 'integrationLevel', message: 'A percent-of-wage-base integration level must be above 0% and at most 100%.' })
    return errors
  }
  if (level.type === 'custom_dollar' && (!Number.isFinite(Number(level.value)) || Number(level.value) <= 0)) {
    errors.push({ field: 'integrationLevel', message: 'A dollar integration level must be a positive amount.' })
    return errors
  }
  const resolvedLevel = resolveIntegrationLevel(level, twb)
  if (!(resolvedLevel > 0)) {
    errors.push({ field: 'integrationLevel', message: 'Integration level could not be resolved for this plan year.' })
    return errors
  }
  if (resolvedLevel > twb * (1 + BAND_EPSILON)) {
    errors.push({ field: 'integrationLevel', message: `Integration level may not exceed the plan-year taxable wage base ($${twb.toLocaleString('en-US')}).` })
    return errors
  }
  if (errors.length) return errors
  const maxDisparity = maxIntegratedDisparityPercent(resolvedLevel, twb)
  if (maxDisparity !== null && disparity > maxDisparity + BAND_EPSILON) {
    errors.push({ field: 'disparityPercent', message: `Maximum disparity at this integration level is ${maxDisparity}% (26 CFR 1.401(l)-2(d)).` })
  }
  if (disparity > base + BAND_EPSILON) {
    errors.push({ field: 'disparityPercent', message: 'Disparity percent may not exceed the base percent (the §401(l) lesser-of rule).' })
  }
  return errors
}

// Pure per-person integrated allocation before §415(c)/compensation caps.
export function integratedRequestedAmount(cappedCompensation, component = {}, integrationLevelDollar) {
  const comp = Math.max(0, Number(cappedCompensation) || 0)
  const base = Math.max(0, Number(component.basePercent) || 0)
  const disparity = Math.max(0, Number(component.disparityPercent) || 0)
  const excess = Math.max(0, comp - Math.max(0, Number(integrationLevelDollar) || 0))
  return (base / 100) * comp + (disparity / 100) * excess
}

function requestIntegratedComponent(employees, component, context = {}) {
  const fieldErrors = validateIntegratedComponent(component, context)
  if (fieldErrors.length) {
    return {
      status: 'requires-review', reason: 'integrated-validation', fieldErrors,
      requestedPool: 0, allocatedPool: 0, unallocatedResidual: 0, allocationsByEmployeeId: {},
    }
  }
  const integrationLevel = resolveIntegrationLevel(component.integrationLevel, context.taxableWageBase)
  const allocationsByEmployeeId = {}
  for (const employee of employees.filter(eligibleForAllocation)) {
    const comp = Math.max(0, Number(employee.cappedCompensation) || 0)
    allocationsByEmployeeId[employee.id] = {
      requested: integratedRequestedAmount(comp, component, integrationLevel),
      allocated: 0,
      capLimited: false,
      excessCompensation: Math.max(0, comp - integrationLevel),
    }
  }
  const requestedPool = Object.values(allocationsByEmployeeId).reduce((sum, row) => sum + row.requested, 0)
  return {
    status: 'requested', requestedPool, allocatedPool: 0, unallocatedResidual: requestedPool,
    allocationsByEmployeeId,
    integrationLevel,
    maxDisparityPercent: maxIntegratedDisparityPercent(integrationLevel, context.taxableWageBase),
  }
}

// --- Uniform points (budget-funded) ------------------------------------------
//
// Points per person: perYearOfAge x whole years of age, plus perYearOfService x
// whole years of service, plus perCompUnit x whole compensation units of
// compUnit dollars. The budget pool is distributed in integer cents,
// proportional to points, by largest remainder; §415(c)/compensation caps then
// apply in the shared allocation loop.

// Blank means "not provided" — never zero. Number(null) is 0, so age and
// service resolution must reject null/''/undefined explicitly (the same rule
// the age-weighted component applies).
const finiteAgeOf = (employee = {}) => {
  if (employee.age === null || employee.age === undefined || employee.age === '') return null
  const age = Number(employee.age)
  return Number.isFinite(age) ? age : null
}

// Calculated rows carry tenure as { years, months, ... }; direct engine
// callers may pass a plain number of years. Points use whole completed years.
export function serviceYearsForEmployee(employee = {}) {
  const tenure = employee.tenure
  if (tenure === null || tenure === undefined || tenure === '') return null
  const years = typeof tenure === 'object' ? Number(tenure.years) : Number(tenure)
  return Number.isFinite(years) ? Math.floor(years) : null
}

export function pointsForEmployee(employee = {}, component = {}) {
  const perAge = Math.max(0, Number(component.perYearOfAge) || 0)
  const perService = Math.max(0, Number(component.perYearOfService) || 0)
  const perUnit = Math.max(0, Number(component.perCompUnit) || 0)
  const unit = Number(component.compUnit)
  const age = finiteAgeOf(employee)
  const serviceYears = serviceYearsForEmployee(employee)
  const comp = Math.max(0, Number(employee.cappedCompensation) || 0)
  let points = 0
  if (perAge > 0 && age !== null) points += perAge * Math.floor(age)
  if (perService > 0 && serviceYears !== null) points += perService * serviceYears
  if (perUnit > 0 && unit > 0) points += perUnit * Math.floor(comp / unit)
  return points
}

function requestPointsComponent(employees, component) {
  const perAge = Math.max(0, Number(component.perYearOfAge) || 0)
  const perService = Math.max(0, Number(component.perYearOfService) || 0)
  const perUnit = Math.max(0, Number(component.perCompUnit) || 0)
  if (!(perAge > 0) && !(perService > 0) && !(perUnit > 0)) {
    return {
      status: 'requires-review', reason: 'points-validation',
      fieldErrors: [{ field: 'perYearOfAge', message: 'A points formula needs at least one positive point weight.' }],
      requestedPool: 0, allocatedPool: 0, unallocatedResidual: 0, allocationsByEmployeeId: {},
    }
  }
  if (perUnit > 0 && !(Number(component.compUnit) > 0)) {
    return {
      status: 'requires-review', reason: 'points-validation',
      fieldErrors: [{ field: 'compUnit', message: 'Compensation unit must be a positive dollar amount.' }],
      requestedPool: 0, allocatedPool: 0, unallocatedResidual: 0, allocationsByEmployeeId: {},
    }
  }
  const requestedPoolCents = cents(component.contributionPool)
  const eligible = employees.filter(eligibleForAllocation)
  const missingAgeEmployeeIds = perAge > 0
    ? eligible.filter((employee) => finiteAgeOf(employee) === null).map((employee) => employee.id)
    : []
  const missingServiceEmployeeIds = perService > 0
    ? eligible.filter((employee) => serviceYearsForEmployee(employee) === null).map((employee) => employee.id)
    : []
  if (missingAgeEmployeeIds.length || missingServiceEmployeeIds.length) {
    return {
      status: 'unavailable',
      reason: missingAgeEmployeeIds.length ? 'missing-age' : 'missing-service',
      requestedPool: requestedPoolCents / 100, allocatedPool: 0,
      unallocatedResidual: requestedPoolCents / 100,
      missingAgeEmployeeIds, missingServiceEmployeeIds, allocationsByEmployeeId: {},
    }
  }
  const weighted = eligible
    .map((employee) => ({ employee, points: pointsForEmployee(employee, component) }))
    .filter((row) => row.points > 0)
  const requestedCents = roundedProRata(
    requestedPoolCents,
    weighted.map((row) => ({ employeeId: row.employee.id, weight: row.points })),
  )
  const allocationsByEmployeeId = {}
  weighted.forEach((row, index) => {
    allocationsByEmployeeId[row.employee.id] = {
      requested: requestedCents[index] / 100,
      allocated: 0,
      capLimited: false,
      points: row.points,
    }
  })
  return {
    status: 'requested',
    requestedPool: requestedPoolCents / 100,
    allocatedPool: 0,
    unallocatedResidual: requestedPoolCents / 100,
    totalPoints: weighted.reduce((sum, row) => sum + row.points, 0),
    missingAgeEmployeeIds,
    missingServiceEmployeeIds,
    allocationsByEmployeeId,
  }
}

// --- Compensation-proportional budget pool -----------------------------------
//
// Budget-funded pro rata: the pool is distributed in integer cents
// proportional to capped compensation by largest remainder, then capped by the
// shared §415(c)/compensation loop. Residual above aggregate headroom is
// reported as unallocated with partially-allocated status.

function requestCompPoolComponent(employees, component) {
  const requestedPoolCents = cents(component.contributionPool)
  const weighted = employees
    .filter(eligibleForAllocation)
    .map((employee) => ({ employee, weight: Math.max(0, Number(employee.cappedCompensation) || 0) }))
    .filter((row) => row.weight > 0)
  const requestedCents = roundedProRata(
    requestedPoolCents,
    weighted.map((row) => ({ employeeId: row.employee.id, weight: row.weight })),
  )
  const allocationsByEmployeeId = {}
  weighted.forEach((row, index) => {
    allocationsByEmployeeId[row.employee.id] = {
      requested: requestedCents[index] / 100,
      allocated: 0,
      capLimited: false,
    }
  })
  return {
    status: 'requested',
    requestedPool: requestedPoolCents / 100,
    allocatedPool: 0,
    unallocatedResidual: requestedPoolCents / 100,
    allocationsByEmployeeId,
  }
}

function requestSimpleComponent(employees, component) {
  const allocationsByEmployeeId = {}
  for (const employee of employees.filter(eligibleForAllocation)) {
    const requested = component.method === 'flat'
      ? Math.max(0, Number(component.amount) || 0)
      : (Math.max(0, Number(component.percent) || 0) / 100) * Number(employee.cappedCompensation || 0)
    allocationsByEmployeeId[employee.id] = { requested, allocated: 0, capLimited: false }
  }
  const requestedPool = Object.values(allocationsByEmployeeId).reduce((sum, row) => sum + row.requested, 0)
  return { status: 'requested', requestedPool, allocatedPool: 0, unallocatedResidual: requestedPool, allocationsByEmployeeId }
}

function requestedFromFormula(employee, formula = {}) {
  if (formula.method === 'flat') return Math.max(0, Number(formula.amount) || 0)
  if (formula.method === 'fill_415c') return baseHeadroomCents(employee) / 100
  return (Math.max(0, Number(formula.percent) || 0) / 100) * Number(employee.cappedCompensation || 0)
}

// --- Structured group-ID classes (§2.5b) -------------------------------------
//
// Membership arrives pre-resolved as context.resolvedGroupIds[employeeId]
// .profitSharing (exact ID matching, never label normalization). An employee
// whose resolved group has no formula gets ZERO for this source plus a review
// listing — it never borrows another group's formula.

function requestGroupClassesComponent(employees, component, context = {}) {
  const resolvedGroupIds = context.resolvedGroupIds || {}
  const groupLabelsById = context.groupLabelsById || {}
  const groupIds = (component.groups || []).map((group) => group.groupId)
  if (new Set(groupIds).size !== groupIds.length) {
    return {
      status: 'requires-review', reason: 'duplicate-group-formulas',
      fieldErrors: [{ field: 'groups', message: 'Each allocation class may have at most one rate.' }],
      requestedPool: 0, allocatedPool: 0, unallocatedResidual: 0, allocationsByEmployeeId: {},
    }
  }
  const byGroupId = new Map((component.groups || []).map((group) => [group.groupId, group]))
  const allocationsByEmployeeId = {}
  const missingFormulaEmployeeIds = []
  for (const employee of employees.filter(eligibleForAllocation)) {
    const groupId = resolvedGroupIds[employee.id]?.profitSharing ?? null
    const group = groupId ? byGroupId.get(groupId) : null
    if (!group) {
      missingFormulaEmployeeIds.push(employee.id)
      continue
    }
    allocationsByEmployeeId[employee.id] = {
      requested: requestedFromFormula(employee, group.formula),
      allocated: 0,
      capLimited: false,
      groupId,
      groupLabel: groupLabelsById[groupId] ?? '',
    }
  }
  const requestedPool = Object.values(allocationsByEmployeeId).reduce((sum, row) => sum + row.requested, 0)
  return {
    status: 'requested', requestedPool, allocatedPool: 0, unallocatedResidual: requestedPool,
    allocationsByEmployeeId, missingFormulaEmployeeIds,
  }
}

// --- Per-person amounts (§2.2) -----------------------------------------------
//
// Amounts live ONLY in the browser-local sidecar and enter through
// context.perPersonAmounts at runtime; the compiled component carries none. A
// participant with no local amount gets zero plus a review listing — never a
// fallback to owners, compensation, or another slot.

function requestPerPersonComponent(employees, component, context = {}) {
  const amounts = context.perPersonAmounts?.[component.source || 'profitSharing'] || {}
  const allocationsByEmployeeId = {}
  const missingAmountEmployeeIds = []
  for (const employee of employees.filter(eligibleForAllocation)) {
    const amount = amounts[employee.id]
    if (!(Number.isFinite(amount) && amount >= 0)) {
      missingAmountEmployeeIds.push(employee.id)
      allocationsByEmployeeId[employee.id] = { requested: 0, allocated: 0, capLimited: false, missingAmount: true }
      continue
    }
    allocationsByEmployeeId[employee.id] = { requested: amount, allocated: 0, capLimited: false }
  }
  const requestedPool = Object.values(allocationsByEmployeeId).reduce((sum, row) => sum + row.requested, 0)
  return {
    status: 'requested', requestedPool, allocatedPool: 0, unallocatedResidual: requestedPool,
    allocationsByEmployeeId, missingAmountEmployeeIds,
  }
}

function requestComponent(employees, component, context) {
  if (component.type === 'age_weighted') return requestAgeWeightedPool(employees, component)
  if (component.type === 'group_classes') return requestGroupClassesComponent(employees, component, context)
  if (component.type === 'per_person') return requestPerPersonComponent(employees, component, context)
  if (component.type === 'integrated') return requestIntegratedComponent(employees, component, context)
  if (component.type === 'points') return requestPointsComponent(employees, component)
  if (component.type === 'comp_pool') return requestCompPoolComponent(employees, component)
  if (component.type === 'simple') return requestSimpleComponent(employees, component)
  return {
    status: 'requires-review', reason: 'unsupported-component', requestedPool: 0,
    allocatedPool: 0, unallocatedResidual: 0, allocationsByEmployeeId: {},
  }
}

// `context` carries plan-year facts individual components need beyond the
// employee rows — today only the taxable wage base for integrated validation.
export function allocateProfitSharingComponents(employees = [], components = [], limitPolicy = {}, context = {}) {
  const list = Array.isArray(components) ? components : []
  const ids = list.map((component) => component.id)
  const order = Array.isArray(limitPolicy.order) ? limitPolicy.order : []
  const ageComponents = list.filter((component) => component.type === 'age_weighted')
  const ageConventions = new Set(ageComponents.map((component) => `${component.assumptionSet}:${Number(component.normalRetirementAge)}`))
  if (ageConventions.size > 1) {
    return {
      status: 'requires-review', reason: 'incompatible-age-weighted-assumptions',
      diagnostics: list.map((component) => ({
        componentId: component.id, componentType: component.type, status: 'requires-review',
        requestedPool: 0, allocatedPool: 0, unallocatedResidual: 0, allocationsByEmployeeId: {},
      })),
      allocationsByEmployeeId: {},
    }
  }
  const validOrder = order.length === ids.length && new Set(order).size === ids.length
    && ids.every((id) => order.includes(id))
  if (!validOrder || limitPolicy.excessHandling !== 'unallocated') {
    return {
      status: 'requires-review', reason: !validOrder ? 'missing-allocation-order' : 'unsupported-excess-handling',
      diagnostics: list.map((component) => ({
        componentId: component.id, componentType: component.type, status: 'requires-review',
        requestedPool: 0, allocatedPool: 0, unallocatedResidual: 0, allocationsByEmployeeId: {},
      })),
      allocationsByEmployeeId: {},
    }
  }
  const byId = new Map(list.map((component) => [component.id, component]))
  const remaining = new Map(employees.map((employee) => [employee.id, baseHeadroomCents(employee)]))
  const combined = {}
  const diagnostics = []
  for (const componentId of order) {
    const component = byId.get(componentId)
    const diagnostic = {
      componentId, componentType: component.type,
      ...requestComponent(employees, component, context),
    }
    let allocatedPoolCents = 0
    let capLimitedEmployeeCount = 0
    if (!['unavailable', 'requires-review'].includes(diagnostic.status)) {
      for (const [employeeId, allocation] of Object.entries(diagnostic.allocationsByEmployeeId || {})) {
        const requestedCents = cents(allocation.requested)
        const allocatedCents = Math.min(requestedCents, remaining.get(employeeId) || 0)
        remaining.set(employeeId, Math.max(0, (remaining.get(employeeId) || 0) - allocatedCents))
        allocation.allocated = allocatedCents / 100
        allocation.capLimited = allocatedCents < requestedCents
        if (allocation.capLimited) capLimitedEmployeeCount += 1
        allocatedPoolCents += allocatedCents
        const aggregate = combined[employeeId] || { amount: 0, ageWeightedAmount: 0, components: [] }
        aggregate.amount += allocation.allocated
        if (component.type === 'age_weighted') {
          aggregate.ageWeightedAmount += allocation.allocated
          aggregate.testingAge = allocation.testingAge
          aggregate.factor = allocation.factor
        }
        aggregate.capLimited ||= allocation.capLimited
        aggregate.components.push({ componentId, type: component.type, ...allocation })
        combined[employeeId] = aggregate
      }
      diagnostic.allocatedPool = allocatedPoolCents / 100
      diagnostic.unallocatedResidual = Math.max(0, diagnostic.requestedPool - diagnostic.allocatedPool)
      diagnostic.capLimitedEmployeeCount = capLimitedEmployeeCount
      diagnostic.status = diagnostic.unallocatedResidual > 0.005 ? 'partially-allocated' : 'ok'
      if (diagnostic.groupDiagnostics) {
        for (const groupDiagnostic of diagnostic.groupDiagnostics) {
          const memberAllocations = Object.values(diagnostic.allocationsByEmployeeId)
            .filter((allocation) => allocation.groupId === groupDiagnostic.groupId)
          groupDiagnostic.allocatedPool = memberAllocations.reduce((sum, allocation) => sum + allocation.allocated, 0)
          groupDiagnostic.unallocatedResidual = Math.max(0, groupDiagnostic.requestedPool - groupDiagnostic.allocatedPool)
          groupDiagnostic.capLimitedEmployeeCount = memberAllocations.filter((allocation) => allocation.capLimited).length
          groupDiagnostic.status = groupDiagnostic.unallocatedResidual > 0.005 ? 'partially-allocated' : 'ok'
        }
      }
    }
    diagnostics.push(diagnostic)
  }
  return {
    status: diagnostics.some((item) => item.status === 'unavailable') ? 'unavailable'
      : diagnostics.some((item) => item.status === 'requires-review') ? 'requires-review'
        : diagnostics.some((item) => item.status === 'partially-allocated') ? 'partially-allocated' : 'ok',
    diagnostics,
    allocationsByEmployeeId: combined,
  }
}

export function allocateAgeWeightedPool(employees = [], component = {}, context = {}) {
  return allocateProfitSharingComponents(
    employees,
    [component],
    { order: [component.id], excessHandling: 'unallocated' },
    context,
  ).diagnostics[0]
}
